import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ThumbnailList } from '../ThumbnailList';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../Accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';

const StudyItem = ({
  date,
  description,
  numInstances,
  modalities,
  patientName,
  isActive,
  onClick,
  isExpanded,
  displaySets,
  activeDisplaySetInstanceUIDs,
  onClickThumbnail,
  onDoubleClickThumbnail,
  onClickUntrack,
  viewPreset = 'thumbnails',
  ThumbnailMenuItems,
  StudyMenuItems,
  StudyInstanceUID,
  onEditDescription,
}: withAppTypes) => {
  return (
    <Accordion
      type="single"
      collapsible
      onClick={onClick}
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
      defaultValue={isActive ? 'study-item' : undefined}
    >
      <AccordionItem value="study-item">
        <AccordionTrigger className={classnames('hover:bg-accent bg-popover group w-full rounded', {
          'border-l-4 border-l-blue-500 bg-blue-900/20': isActive, // Highlight active study
        })}>
          <div className={classnames('flex w-full flex-row overflow-hidden py-1', {
            'min-h-[56px]': patientName, // Taller when showing patient name
            'h-[40px]': !patientName, // Original height without patient name
          })}>
            <div className="flex w-full flex-row items-center justify-between">
              <div className="flex min-w-0 flex-col items-start text-[13px]">
                {patientName && (
                  <Tooltip>
                    <TooltipContent>{patientName}</TooltipContent>
                    <TooltipTrigger
                      className="w-full"
                      asChild
                    >
                      <div className="h-[18px] w-full max-w-[160px] overflow-hidden truncate whitespace-nowrap text-left text-white font-medium text-[11px]">
                        {patientName}
                      </div>
                    </TooltipTrigger>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipContent>{date}</TooltipContent>
                  <TooltipTrigger className="w-full" asChild>
                    <div className="text-[12px] text-white whitespace-nowrap">{date}</div>
                  </TooltipTrigger>
                </Tooltip>
              </div>
            </div>

            {/* Bottom Row: Modality • Description (left) | Count (right) */}
            <div className="flex w-full items-center justify-between pl-[18px]">
              <div className="flex items-center gap-[6px] min-w-0 flex-1">
                <div className="text-[11px] text-muted-foreground whitespace-nowrap">{modalities?.substring(0, 2)}</div>
                <div className="text-muted-foreground">•</div>
                <Tooltip>
                  <TooltipContent>{description}</TooltipContent>
                  <TooltipTrigger className="min-w-0" asChild>
                    <div className="text-[11px] text-muted-foreground truncate">{description}</div>
                  </TooltipTrigger>
                </Tooltip>
              </div>
              <div className="text-[11px] text-muted-foreground ml-2 whitespace-nowrap">{numInstances}</div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent
          onClick={event => {
            event.stopPropagation();
          }}
        >
          {isExpanded && displaySets && (
            <ThumbnailList
              thumbnails={displaySets}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              onThumbnailClick={onClickThumbnail}
              onThumbnailDoubleClick={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              onEditDescription={onEditDescription}
            />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

StudyItem.propTypes = {
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  modalities: PropTypes.string.isRequired,
  numInstances: PropTypes.number.isRequired,
  patientName: PropTypes.string,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
  displaySets: PropTypes.array,
  activeDisplaySetInstanceUIDs: PropTypes.array,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  viewPreset: PropTypes.string,
  StudyMenuItems: PropTypes.func,
  StudyInstanceUID: PropTypes.string,
  onEditDescription: PropTypes.func,
};

export { StudyItem };
